/**
 * Render pipeline — the single source of truth that turns raw markdown into
 * the HTML, table-of-contents, and meta a consumer needs. Every surface
 * (live preview, share-URL landing, exported HTML, print stylesheet target)
 * calls this same function with the same options. Forking the pipeline per
 * consumer is how Codex anti-pattern #4 (different render rules across
 * surfaces) creeps in; keep it here.
 *
 * Pipeline order (Phase 4):
 *   raw markdown
 *     → extractMermaid   (```mermaid``` → placeholder <div>, diagrams[] pushed)
 *     → extractMath      ($..$ / $$..$$ / ```math``` → MATHPH<N>, KaTeX html pushed)
 *     → markdown-it      (html=true so the placeholder divs survive)
 *     → plugins: footnote, task-lists, emoji.full, anchor (custom slugify),
 *                calloutPlugin (GitHub alerts + Obsidian aliases)
 *     → DOMPurify sanitize (single chokepoint — placeholder divs on allowlist)
 *     → restore math     (splice KaTeX html back; not double-sanitized)
 *     → buildToc         (heading tree)
 *     → { html, toc, meta, mermaidDiagrams }
 *
 * The consumer (Preview.svelte, Phase 6) is responsible for calling
 * hydrateMermaid + hydrateCode AFTER the html lands in the DOM, since both
 * lazy-import their engines and need a mounted container to walk.
 *
 * `html: true` here is intentional: it lets our pre-extracted mermaid
 * placeholder divs survive markdown-it's block tokenizer. DOMPurify is the
 * real safety boundary, not markdown-it's html flag, and the share-URL
 * threat model assumes every document is attacker-controlled — sanitize
 * has the same allowlist as before, just sees more raw HTML now.
 */

import MarkdownIt from 'markdown-it'
import footnote from 'markdown-it-footnote'
import taskLists from 'markdown-it-task-lists'
import { full as emoji } from 'markdown-it-emoji'
import anchor from 'markdown-it-anchor'
import { sanitize } from './sanitize'
import { buildToc, type TocNode } from './toc'
import { calloutPlugin } from './callout'
import { extractMath } from './math'
import { extractMermaid } from './mermaid'
import { uniqueSlug } from './slug'
import { getDocTitle } from './title'

export type { TocNode } from './toc'

export interface DocMeta {
  /** First H1 in the source, or "Untitled" when none is present. */
  title: string
  /** Word count excluding fenced code, indented code, and inline code spans. */
  wordCount: number
  /** Word count divided by 220 wpm, rounded up to the nearest minute. */
  readingMinutes: number
}

export interface RenderResult {
  html: string
  toc: TocNode[]
  meta: DocMeta
  /**
   * Raw mermaid sources in document order. The consumer calls
   * hydrateMermaid(container, mermaidDiagrams, mermaidNonce, options?) after
   * the html lands in the DOM. Empty when the source has no ```mermaid fences.
   */
  mermaidDiagrams: string[]
  /**
   * Per-render nonce stamped on every mermaid placeholder div. The consumer
   * MUST pass this to hydrateMermaid so user-pasted literal placeholder HTML
   * (now possible since markdown-it html:true) can't spoof a hydration target.
   * P1.5 from the 2026-05-23 codex audit.
   */
  mermaidNonce: string
}

// Slugify is lock-step with the heading-sync map (scrollSync.ts Phase 9):
// lowercase, trim, drop everything that isn't a word char / space / hyphen,
// replace spaces with hyphens. The shared `uniqueSlug` from ./slug.ts also
// disambiguates duplicate headings (`#topic`, `#topic-1`, `#topic-2`) using
// a per-render counter. Both this anchor plugin AND scrollSync.ts import
// from the same module so editor→preview scroll lands on the right anchor
// even for docs with repeated headings.
//
// markdown-it-anchor's slugify hook is called once per heading and we need
// the SAME per-document counter to live across all those calls. The shared
// `slugMap` is reset at the top of every render() invocation (see below).

let slugMap = new Map<string, number>()

function slugify(text: string): string {
  return uniqueSlug(text, slugMap)
}

// Single shared instance — markdown-it is stateless across render calls and
// building one per render adds noticeable cost on long documents.
const md = new MarkdownIt({
  // html: true is required so the mermaid placeholder divs survive the
  // block tokenizer. DOMPurify is the real chokepoint and only allowlisted
  // tags + attrs make it to the DOM; markdown-it's html flag is not a
  // security layer here.
  html: true,
  breaks: false, // GitHub-flavoured: single newlines collapse, not <br>.
  linkify: true, // Bare URLs become anchor tags.
  typographer: true, // Smart quotes + ellipsis + en/em-dash.
})

md.use(footnote)
md.use(taskLists, { enabled: true, lineNumber: false })
md.use(emoji)
md.use(anchor, { slugify })
md.use(calloutPlugin)

// Wrap every table in a horizontal-scroll container. Wide tables (think
// 5+ columns or columns with long unbreakable identifiers like file
// paths or hostnames) used to crush column widths inside the prose
// measure, forcing the browser into mid-word breaks. With this wrapper
// the table can extend beyond the prose width and the wrapper provides
// its own scrollbar — preview body above and below stays put.
//
// DOMPurify's allowlist already permits div with class, so the wrapper
// survives the sanitize pass unchanged.
md.renderer.rules['table_open'] = (tokens, idx, options, _env, self) => {
  return '<div class="table-scroll">' + self.renderToken(tokens, idx, options)
}
md.renderer.rules['table_close'] = (tokens, idx, options, _env, self) => {
  return self.renderToken(tokens, idx, options) + '</div>'
}

// ---- meta helpers -------------------------------------------------------

// Strip fenced code, indented code, and inline code spans before splitting
// on whitespace. Keeps prose word counts honest — a long code dump
// shouldn't inflate the "5 minute read" estimate.
export function countWords(source: string): number {
  const withoutFences = source.replace(/```[\s\S]*?```/g, ' ')
  const withoutIndented = withoutFences
    .split('\n')
    .map((line) => (/^( {4,}|\t)/.test(line) ? '' : line))
    .join('\n')
  const withoutInline = withoutIndented.replace(/`[^`]*`/g, ' ')
  const words = withoutInline.match(/\S+/g)
  return words?.length ?? 0
}

const WORDS_PER_MINUTE = 220

export function readingMinutesFromSource(source: string): number {
  const words = countWords(source)
  if (words <= 0) return 0
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))
}

function readingMinutes(words: number): number {
  if (words <= 0) return 0
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))
}

// ---- public API ---------------------------------------------------------

export interface RenderOptions {
  /** Mermaid theme override; consumer reads `result.mermaidNonce` and passes it
   *  alongside this when calling hydrateMermaid. */
  mermaidTheme?: 'default' | 'dark' | 'neutral' | 'forest'
}

export function render(source: string, _opts: RenderOptions = {}): RenderResult {
  // Reset per-render slug counter so heading IDs don't bleed across docs.
  // (Single shared `md` instance + module-level `slugMap` means we have to
  // do this here, not in the slugify closure.)
  slugMap = new Map<string, number>()

  // Phase 4 extension pipeline. Mermaid first so its placeholder divs go
  // through markdown-it as raw HTML; math next so its placeholder tokens
  // are seen as plain text by markdown-it (no $ chars left to confuse the
  // tokenizer or get smart-quoted). Both use per-render nonces (P1.4 + P1.5)
  // so a user pasting the literal placeholder pattern in their source can't
  // collide with the extraction or spoof Mermaid hydration.
  const { protected: afterMermaid, diagrams, nonce: mermaidNonce } = extractMermaid(source)
  const { protected: afterMath, restore: restoreMath } = extractMath(afterMermaid)

  const rawHtml = md.render(afterMath)
  const sanitized = sanitize(rawHtml)
  const html = restoreMath(sanitized)
  const toc = buildToc(html)
  const wordCount = countWords(source)
  const meta: DocMeta = {
    title: getDocTitle(source),
    wordCount,
    readingMinutes: readingMinutes(wordCount),
  }
  return { html, toc, meta, mermaidDiagrams: diagrams, mermaidNonce }
}
